const LEVELS = {
    1: { name: "Foundation", full: "Level 1 · Foundation" },
    2: { name: "Practitioner", full: "Level 2 · Practitioner" },
    3: { name: "Practitioner", full: "Level 3 · Practitioner" },
    4: { name: "Advanced", full: "Level 4 · Advanced" },
    5: { name: "Advanced", full: "Level 5 · Advanced" },
};

const dlpSections = [
    {
        id: "getting-started",
        title: "Getting Started",
        note: "Level 1 — watch these three first, every later module assumes them",
        lv: 1,
        cards: [
            {
                t: "Agent Deployment and Device Onboarding",
                icon: "download",
                path: [
                    ["Overview", "Getting Started", "Windows"],
                    ["Device Inventory", "Onboarding Requests"],
                ],
                d: "From installer to managed device — build the MSI, push it across the estate, approve what enrolls.",
                learn: [
                    "Generate and download the MSI built for a specific organization",
                    "Deploy the agent across an estate through a Group Policy Object",
                    "Approve and deny enrollment requests, and explain why approval is a separate step",
                    "Diagnose a device that installed but never appeared in the console",
                ],
                chapters: [
                    "Downloading the packaged agent and onboarding manual",
                    "Prerequisites: .NET Desktop Runtime and OS support",
                    "Publishing the install through a Group Policy Object",
                    "Where the macOS agent differs",
                    "Reading the onboarding request list",
                    "Approve and deny from the actions menu",
                    "Confirming the device lands in the device list",
                ],
                videoId: "Jps4yNNDRdQ",
                videoType: "youtube",
                soon: false,
            },
            {
                t: "Devices, Groups and How Policies Work",
                icon: "groups",
                path: [
                    ["Device Inventory", "Devices"],
                    ["Device Inventory", "Device Groups"],
                ],
                d: "The device record and remote actions, then the group and policy structure everything else depends on.",
                learn: [
                    "Read every tab on the device record and run the remote actions safely",
                    "Design device groups around behavior rather than the org chart",
                    "Build any policy in the product from its common parts",
                    "Sequence a rollout through Log, then Notify, then Block",
                ],
                chapters: [
                    "Device ID, agent version, status and system details",
                    "Associated groups, policies and device logs",
                    "Remote lock, restart, shutdown and wipe",
                    "Why policies bind to groups, not individual devices",
                    "Designing groups around behavior, not the org chart",
                    "The common parts of every policy: name, action, group, alert",
                    "Block, Notify and Log — and why rollout order matters",
                ],
                videoId: "vEGZyRgj2eU",
                videoType: "youtube",
                soon: false,
            },
        ],
    },
    {
        id: "peripheral",
        title: "Peripheral & Device Control",
        note: "Level 2 — the three peripheral channels, all built inventory-first then policy",
        lv: 2,
        cards: [
            {
                t: "USB Storage Control",
                icon: "usb",
                path: ["Endpoint", "System Hardening", "USB Storage"],
                d: "Build a USB inventory, whitelist approved drives, and block everything else.",
                learn: [
                    "Find a device's Vendor and Product ID from Device Manager",
                    "Build a USB inventory and whitelist an approved drive",
                    "Choose between Block, Notify and Log",
                ],
                chapters: [
                    "Finding Vendor and Product ID in Device Manager",
                    "Adding an approved drive to the USB inventory",
                    "Block, Notify and Log — what the user sees in each",
                    "The incident report naming the device that was connected",
                ],
                videoId: "WGU-h4Wglro",
                videoType: "youtube",
                soon: false,
            },
            {
                t: "Printer Control",
                icon: "print",
                path: ["Endpoint", "Information Protection", "Printer Control"],
                d: "The same whitelist pattern applied to print, plus one limitation to disclose.",
                learn: [
                    "Whitelist an approved printer by Vendor and Product ID",
                    "Build and test a print restriction policy",
                    "Know why network printers can't be whitelisted",
                ],
                chapters: [
                    "Capturing printer Vendor and Product ID",
                    "Building the printer inventory and policy",
                    "Testing an approved and an unapproved printer",
                ],
                videoId: "Tq15-4S8xqY",
                videoType: "youtube",
                soon: false,
            },
            {
                t: "Bluetooth Control",
                icon: "bluetooth",
                path: ["Endpoint", "Information Protection", "Bluetooth Control"],
                d: "Close the wireless gap that USB and printer control leave open.",
                learn: [
                    "Build an inventory of approved Bluetooth devices",
                    "Apply a Bluetooth policy to a device group with Block, Notify or Log",
                    "Explain why blocking USB alone leaves an open channel",
                ],
                chapters: [
                    "The wireless gap USB and printer control do not cover",
                    "Adding approved devices to the Bluetooth inventory",
                    "Building the policy: action, risk and device group",
                    "Testing an approved and an unapproved device",
                    "Where Bluetooth incidents appear under Reports",
                ],
                videoId: "F36j2jtiyx4",
                videoType: "youtube",
                soon: false,
            },
        ],
    },
    {
        id: "screen-capture",
        title: "Screen & Capture Control",
        note: "Level 2 — for regulated floors: call centres, BPO seats, trading desks",
        lv: 2,
        cards: [
            {
                t: "Screenshot policy — monitoring and blocking",
                icon: "screenshot_monitor",
                path: ["Endpoint", "Information Protection", "Screenshot Policy"],
                d: "Monitor or block screen captures, with interval-based and dual-display options.",
                learn: [
                    "Configure screen monitoring with an interval and dual-display capture",
                    "Apply screenshot blocking to a restricted group",
                    "Know why blocking and monitoring can't run together",
                ],
                chapters: [
                    "Screen monitoring: interval, target devices, dual-display capture",
                    "Screenshot blocking, where the action is fixed to Block",
                    "Finding captures under Reporting",
                ],
                videoId: "p430LN5_6Bg",
                videoType: "youtube",
                soon: false,
            },
            {
                t: "Camera Policy",
                icon: "photo_camera",
                path: ["Endpoint", "System Hardening", "Camera Hardware"],
                d: "Interval-based webcam capture with timestamped, device-linked logs.",
                learn: [
                    "Set a capture interval and choose target devices",
                    "Review captured images filtered by device and date",
                    "Position camera capture as a compliance control",
                ],
                chapters: [
                    "Setting the capture interval and target devices",
                    "Reviewing images under Reporting",
                ],
                videoId: "LGU1-l-qLIY",
                videoType: "youtube",
                soon: false,
            },
            {
                t: "Clipboard Policy",
                icon: "content_paste_off",
                path: ["Endpoint", "Information Protection", "Clipboard Policy"],
                d: "Close copy and paste as a channel, where no file ever moves.",
                learn: [
                    "Restrict copy and paste on managed devices",
                    "Apply the policy to the right device groups without breaking normal work",
                    "Explain how it complements the browser upload policy",
                ],
                chapters: [
                    "The copy-and-paste gap upload control does not cover",
                    "Building the clipboard policy: action, risk and device group",
                    "Applying it alongside screenshot blocking",
                    "What the user sees when a paste is refused",
                    "Where clipboard incidents appear under Reports",
                ],
                videoId: "oJRe--r0lLo",
                videoType: "youtube",
                soon: false,
            },
        ],
    },
    {
        id: "monitoring",
        title: "Monitoring & Hardening",
        note: "Level 2 — the evidence layer, plus the workflow that keeps policies switched on",
        lv: 2,
        cards: [
            {
                t: "File Activity Tracking",
                icon: "folder_limited",
                path: ["Endpoint", "Information Protection", "File Activity Tracking"],
                d: "Log create, modify, delete and move activity on the directories that matter.",
                learn: [
                    "Monitor create, modify, delete and move on named directories",
                    "Read a file activity report: user, device, action and time",
                    "Scope directories proportionately instead of monitoring whole disks",
                ],
                chapters: [
                    "Choosing directories and which file actions to log",
                    "Reading file activity reports",
                    "Scoping to folders that matter rather than whole disks",
                ],
                videoId: "wgQ4zpAntc8",
                videoType: "youtube",
                soon: false,
            },
            {
                t: "User Device Events & Registry Editor",
                icon: "history",
                path: ["Endpoint", "System Hardening"],
                d: "Login and authentication monitoring, plus pushing Windows settings estate-wide.",
                learn: [
                    "Track sign-in, authentication failure and password change events",
                    "Push a registry configuration from the default library",
                    "Use enforced configuration so a setting survives a user reversing it",
                ],
                chapters: [
                    "Tracking sign-in and authentication events",
                    "Default registry configurations",
                    "Enforced configuration, reapplied at every login",
                ],
                videoId: "nT9fS0TT7MI",
                videoType: "youtube",
                soon: false,
            },
            {
                t: "Policy Relaxations",
                icon: "schedule",
                path: ["Endpoint", "System Hardening", "Policy Relaxations"],
                d: "Time-bound, audited exceptions that revert automatically.",
                learn: [
                    "Grant a time-bound exception with a mandatory recorded reason",
                    "Show the exception on the device record and watch it expire",
                    "Answer the objection that stalls DLP rollouts most often",
                ],
                chapters: [
                    "Creating an approval: device, destination, window, remarks",
                    "Watching the exception appear on the device record",
                    "Confirming the policy resumes automatically",
                ],
                videoId: "QXQnCWLr3Tw",
                videoType: "youtube",
                soon: false,
            },
        ],
    },
    {
        id: "web-network",
        title: "Web & Network Control",
        note: "Level 3 — the highest-volume leak channel, and where a device may connect",
        lv: 3,
        cards: [
            {
                t: "Website & Upload Control",
                icon: "public",
                path: ["Endpoint", "Information Protection", "Websites"],
                d: "The full HTTP policy across all six tabs, plus domains and categories.",
                learn: [
                    "Build domains, categories and regex-based category sets",
                    "Construct an HTTP policy across all six tabs",
                    "Predict the blast radius of Allow Domains against Block Domains",
                    "Restrict uploads by file extension and attach a classification action",
                ],
                chapters: [
                    "Why network interception must be enabled first",
                    "Domains, categories and the wildcard rule people get wrong",
                    "Allow Domains against Block Domains, and the blast radius of each",
                    "File restriction by extension",
                    "Attaching a classification with Block, Log or Notify",
                    "Bulk-uploading categories from CSV",
                ],
                videoId: "3Nrc0LAQmw8",
                videoType: "youtube",
                soon: false,
            },
            {
                t: "SMTP & FTP Policy",
                icon: "dns",
                path: ["Endpoint", "Information Protection", "SMTP & FTP Policy"],
                d: "Control outbound SMTP mail relay and FTP file transfers directly from the endpoint.",
                videoId: "hW_0itVz-jQ",
                videoType: "youtube",
                soon: false,
            },
            {
                t: "Wi-Fi & Ethernet Control",
                icon: "wifi",
                path: ["Endpoint", "Connectivity & Identity"],
                d: "Whitelist networks by SSID and BSSID, and gate the wired port.",
                learn: [
                    "Capture a network's SSID and BSSID",
                    "Whitelist approved networks without breaking remote working",
                    "Apply Ethernet control safely",
                ],
                chapters: [
                    "Capturing SSID and BSSID",
                    "Building the Wi-Fi inventory and policy",
                    "Ethernet control — and where not to apply it",
                ],
                videoId: "g5Ql92cdzdg",
                videoType: "youtube",
                soon: false,
            },
            {
                t: "AD User Sessions and Password Policy",
                icon: "password",
                path: ["Endpoint", "Connectivity & Identity"],
                d: "Concurrent session limits, idle timeout and password or PIN rules.",
                learn: [
                    "Set concurrent session limits and choose the right exceed action",
                    "Apply time-based and IP-based adaptive restrictions",
                    "Order password policies by priority so the right one wins",
                ],
                chapters: [
                    "Concurrent session limits and the exceed actions",
                    "Idle timeout and administrator notification",
                    "Time-based and IP-based adaptive rules",
                    "Password, PIN and account lockout settings, ordered by priority",
                ],
                videoId: "2dkh1AlTksU",
                videoType: "youtube",
                soon: false,
            },
        ],
    },
    {
        id: "application-control",
        title: "Application Control",
        note: "Level 3 — see what's installed, stop what shouldn't run, control which accounts sign in",
        lv: 3,
        cards: [
            {
                t: "Software Inventory & Executable Restriction",
                icon: "apps",
                path: ["Endpoint", "App Management"],
                d: "Manage software inventory, remotely uninstall applications, and restrict executable categories across devices.",
                learn: [
                    "View an estate-wide software inventory by name, publisher, version, and device",
                    "Remotely uninstall applications across selected devices",
                    "Create application categories and blacklist them to restrict executable launches",
                    "Configure device groups and alerting for application restriction policies",
                ],
                chapters: [
                    "Reading the software and executable inventory",
                    "Seeing every device an application is installed on",
                    "Remotely uninstalling applications across selected devices",
                    "Why executable restriction uses a blacklist model",
                    "Blocking applications by category",
                    "What users see when an application is refused",
                ],
                videoId: "8oGwsr58pz4",
                videoType: "youtube",
                soon: false,
            },
            {
                t: "Cloud Application Login Control",
                icon: "cloud",
                path: ["Endpoint", "App Management", "Cloud Apps"],
                d: "Control which cloud accounts can sign in from a managed device.",
                learn: [
                    "Register a cloud application with its login URL",
                    "Choose between exact URL matching and broader domain matching",
                    "Avoid the guest-access trap that blocks shared documents",
                ],
                chapters: [
                    "Registering a cloud application with its login URL",
                    "Match Exact URL against Check Domain In URL",
                    "Allowed domains so the corporate tenant keeps working",
                ],
                videoId: "KDOKORsuw6g",
                videoType: "youtube",
                soon: false,
            },
        ],
    },
    {
        id: "data-discovery",
        title: "Data Discovery",
        note: "Level 4 — find sensitive data before deciding what to control",
        lv: 4,
        cards: [
            {
                t: "Content Filtering — Classifications & Rules",
                icon: "sell",
                path: ["Data Loss Prevention", "Content Filtering"],
                d: "Build the classifications that every other DLP policy depends on.",
                learn: [
                    "Explain the chain from rule to classification to policy action",
                    "Write regex and keyword rules that survive real data",
                    "Reuse one classification across web, email and discovery policies",
                ],
                chapters: [
                    "The chain from rule to classification to policy action",
                    "Writing regex and keyword criteria",
                    "Reusing one classification across policies",
                ],
                videoId: "UcaK24b70y0",
                videoType: "youtube",
                soon: false,
            },
        ],
    },
    {
        id: "email-dlp",
        title: "Email DLP",
        note: "Level 5 — protects mail from any device on the account, including phones",
        lv: 5,
        cards: [
            {
                t: "Email DLP",
                icon: "forward_to_inbox",
                path: ["Data Loss Prevention", "Email"],
                d: "An end-to-end walkthrough of email DLP — provider setup through outbound policy and quarantine.",
                learn: [
                    "Integrate a mail provider — Google Workspace, Microsoft 365, Zoho or Mithi",
                    "Design an outbound policy using Block, Log or Quarantine",
                    "Operate the quarantine queue and agree who owns it before go-live",
                ],
                videoId: "1zvMbmnLfQg",
                videoType: "youtube",
                soon: false,
            },
        ],
    },
];

export { dlpSections, LEVELS };
